import * as THREE from 'three'

/**
 * Per-grid-cell view-space ray directions, normalised so `z === -1`. A view-space
 * point is then simply `dir * depthMetres`.
 *
 * Depends only on the projection matrix and the depth UV transform, so it
 * survives every frame the device orientation holds — which is why it is cached
 * behind a signature rather than recomputed per frame.
 *
 * Reads from `dirs` and the two matrices are asserted non-null: every index is
 * derived from the table's own dimensions.
 */
export interface RayTable {
  dirs: Float32Array
  gridWidth: number
  gridHeight: number
  stride: number
  signature: string
}

function signatureOf(
  projectionMatrix: Float32Array,
  uvTransform: Float32Array,
  width: number,
  height: number,
  stride: number
): string {
  const p = [0, 5, 8, 9].map((i) => projectionMatrix[i]!.toFixed(5)).join(',')
  const uv = [0, 1, 4, 5].map((i) => uvTransform[i]!.toFixed(4)).join(',')
  return `${width}x${height}/${stride}/${p}/${uv}`
}

export function buildRayTable(
  projectionMatrix: Float32Array,
  uvTransform: Float32Array,
  width: number,
  height: number,
  stride: number,
  previous: RayTable | null
): RayTable {
  const signature = signatureOf(projectionMatrix, uvTransform, width, height, stride)
  if (previous && previous.signature === signature) return previous

  const invProjection = new THREE.Matrix4().fromArray(projectionMatrix).invert()
  const invUv = new THREE.Matrix4().fromArray(uvTransform).invert()

  const gridWidth = Math.floor(width / stride)
  const gridHeight = Math.floor(height / stride)
  const dirs = new Float32Array(gridWidth * gridHeight * 3)
  const point = new THREE.Vector4()

  for (let j = 0; j < gridHeight; j += 1) {
    const depthV = (j * stride + 0.5) / height
    for (let i = 0; i < gridWidth; i += 1) {
      const depthU = (i * stride + 0.5) / width

      // invUv yields *normalised view coordinates*: origin top-left, y growing
      // DOWN (WebXR Depth Sensing spec). NDC is y-up, hence `1 - 2v`, matching
      // Chromium's own screen-to-ray conversion in `arcore_gl.cc`.
      point.set(depthU, depthV, 0, 1).applyMatrix4(invUv)
      point.set(point.x * 2 - 1, 1 - point.y * 2, -1, 1).applyMatrix4(invProjection)

      const invW = 1 / point.w
      const z = point.z * invW
      const scale = z === 0 ? 0 : -1 / z

      const offset = (j * gridWidth + i) * 3
      dirs[offset] = point.x * invW * scale
      dirs[offset + 1] = point.y * invW * scale
      dirs[offset + 2] = -1
    }
  }

  return { dirs, gridWidth, gridHeight, stride, signature }
}
